import { useState } from 'react';
import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema } from '@/components/seo/seo';

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

const faqs: FAQItem[] = [
  {
    question: "What is Fixtyer?",
    answer: (
      <p>
        Mac helps drivers in the UK claim compensation for vehicle damage caused by potholes. 
        Mac guides you through the process and prepares your claim documents — you submit to the 
        council yourself.
      </p>
    ),
  },
  {
    question: "Who can make a pothole claim?",
    answer: (
      <>
        <p className="mb-3">You can usually make a claim if:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Your vehicle was damaged by a pothole on a public road</li>
          <li>The road is maintained by a local council, National Highways, or another highway authority</li>
          <li>You have evidence of the damage and where it occurred</li>
        </ul>
        <p className="mt-3 text-surface-800 italic">
          Private roads, car parks, and unadopted roads are usually excluded.
        </p>
      </>
    ),
  },
  {
    question: "What kind of damage can I claim for?",
    answer: (
      <>
        <p className="mb-3">Typical claims include:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Tyre damage (bulges, blowouts)</li>
          <li>Cracked or buckled alloy wheels</li>
          <li>Suspension or steering damage</li>
          <li>Tracking or alignment issues</li>
        </ul>
        <p className="mt-3">If you're unsure whether your damage qualifies, Mac will check for you.</p>
      </>
    ),
  },
  {
    question: "How does the claims process work?",
    answer: (
      <>
        <ol className="list-decimal pl-5 space-y-2">
          <li>You provide incident details and evidence to Fixtyer</li>
          <li>Mac suggests the highway authority likely to be responsible for you to review</li>
          <li>Mac helps organise the evidence and prepare claim documents</li>
          <li>You review the documents, submit the claim yourself, and manage all correspondence</li>
          <li>You decide whether and how to follow up or respond</li>
        </ol>
        <p className="mt-3 font-medium text-brand-700">
          Fixtyer does not submit or track claims, contact the authority, or negotiate. No outcome is promised.
        </p>
      </>
    ),
  },
  {
    question: "What evidence do I need?",
    answer: (
      <>
        <p className="mb-3">Strong claims typically include:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Photos of the pothole (ideally showing depth and location)</li>
          <li>Photos of vehicle damage</li>
          <li>Repair invoices or quotes</li>
          <li>Date, time, and exact location of the incident</li>
          <li>Proof of vehicle ownership</li>
        </ul>
        <p className="mt-3 text-brand-600 font-medium">
          Mac prompts you for commonly requested evidence, but the authority may ask for more.
        </p>
      </>
    ),
  },
  {
    question: "How long does a pothole claim take?",
    answer: (
      <p>
        Response times vary by authority and claim. Check the authority&apos;s published process, keep your
        own records, and manage any follow-up directly.
      </p>
    ),
  },
  {
    question: "Is there a time limit to make a claim?",
    answer: (
      <p>
        Yes. You should submit a claim <strong>as soon as possible</strong> after the incident. 
        Delays can weaken your case, especially if the road is repaired before evidence is gathered.
      </p>
    ),
  },
  {
    question: "How much compensation could I receive?",
    answer: (
      <>
        <p className="mb-3">If successful, compensation usually covers:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>The cost of repairs</li>
          <li>In some cases, related expenses (such as recovery)</li>
        </ul>
        <p className="mt-3 text-surface-800 italic">
          Councils do not typically compensate for inconvenience or loss of use.
        </p>
      </>
    ),
  },
  {
    question: "Do councils always pay out?",
    answer: (
      <>
        <p className="mb-3">No. Councils may reject claims if they can show:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>The road was inspected and maintained properly</li>
          <li>The pothole was repaired within reasonable timescales</li>
          <li>They were not aware of the defect</li>
        </ul>
        <p className="mt-3 font-medium text-brand-700">
          Mac organises the information and evidence you provide into documents for you to review.
          This does not determine whether the authority accepts the claim.
        </p>
      </>
    ),
  },
  {
    question: "What does Fixtyer cost?",
    answer: (
      <p>
        Fixtyer operates on a simple, transparent pricing model. Our Council Claim Pack is just <strong>£35</strong> — 
        and you only pay when you're ready to generate your claim documents. Any fees are clearly explained 
        before you proceed — no hidden charges, no surprises.
      </p>
    ),
  },
  {
    question: "What if my claim is rejected?",
    answer: (
      <>
        <p className="mb-3">If a claim is rejected, you can review the response and use Fixtyer&apos;s general information about possible next steps, which may include:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Submitting additional evidence</li>
          <li>Challenging the decision</li>
          <li>Deciding whether further action is worthwhile</li>
        </ul>
        <p className="mt-3">
          You decide how to respond and whether to take further action. Consider independent legal advice
          if you are unsure.
        </p>
      </>
    ),
  },
  {
    question: "Can I make a claim myself without Fixtyer?",
    answer: (
      <>
        <p className="mb-3">
          Yes. Fixtyer provides document-preparation tools and general information, including:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>A guided evidence checklist</li>
          <li>Organisation of the information and evidence you provide</li>
          <li>Editable claim documents for your review</li>
          <li>General information about submitting your own claim</li>
        </ul>
      </>
    ),
  },
  {
    question: "Does Fixtyer guarantee a successful claim?",
    answer: (
      <p>
        No. Decisions are made by highway authorities based on the facts and evidence. Fixtyer prepares
        documents and general information but cannot determine the outcome.
      </p>
    ),
  },
  {
    question: "Is Fixtyer a law firm?",
    answer: (
      <p>
        No. Fixtyer is a self-service document-preparation platform. We do not provide legal advice,
        submit claims, contact authorities, negotiate, or represent customers. You review, submit and
        manage your own claim.
      </p>
    ),
  },
  {
    question: "How do I get started?",
    answer: (
      <p>
        Begin with Fixtyer&apos;s initial check, then provide information and evidence at your own pace.
        Mac prepares documents for you to review before you submit the claim yourself.
      </p>
    ),
  },
];

export function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'FAQs', url: '/faq' },
  ];

  // Convert faqs to schema format (need to extract text from React nodes)
  const faqsForSchema = faqs.map(f => ({
    question: f.question,
    answer: typeof f.answer === 'string' ? f.answer : f.question.includes('What is Fixtyer') 
      ? 'Mac helps drivers in the UK claim compensation for vehicle damage caused by potholes. Mac guides you through the process and prepares your claim documents — you submit to the council yourself.'
      : 'Please see our detailed FAQ section for the complete answer.',
  }));

  return (
    <div className="relative">
      <SEO
        title="Pothole Claim FAQ for England and Wales | Fixtyer"
        description="Clear answers on pothole damage claims in England and Wales: evidence, responsible highway authorities, Section 58, costs and self-service claim packs."
        canonical="/faq"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <FAQSchema faqs={faqsForSchema} />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50 py-16 lg:py-24">
        {/* Top diagonal stripe bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        {/* Background pattern */}
        <div className="absolute inset-0 opacity-30 mt-8">
          <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-brand-200" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-brand-600 mb-6">
            <Link to="/" className="hover:text-brand-700 transition-colors">Home</Link>
            <svg className="w-4 h-4 text-surface-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-surface-800">FAQs</span>
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-100 rounded-full text-accent-700 text-sm font-medium mb-6">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Got questions? We've got answers
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold text-brand-950 leading-tight mb-6">
            Pothole damage claim{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent-500 to-accent-600">
              FAQs
            </span>
          </h1>
          <p className="text-lg text-surface-800 max-w-2xl mx-auto">
            Pothole damage claims in England and Wales depend on the incident evidence, the highway authority&apos;s
            records and the applicable law. These answers explain the self-service process without predicting an outcome.
          </p>
          <p className="mt-4 text-sm font-medium text-surface-600">Last reviewed: 19 July 2026</p>
          <p className="mt-5 text-sm text-surface-700">
            Start with{' '}
            <Link className="font-semibold text-brand-700 underline" to="/guides/what-makes-a-valid-claim">
              the claim evidence guide
            </Link>
            {' '}or{' '}
            <Link className="font-semibold text-brand-700 underline" to="/councils">
              find your highway authority and its road data
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="relative -mt-8 z-10 pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl p-6 shadow-lg shadow-brand-900/5 border border-surface-200 text-center">
              <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="font-display text-2xl font-bold text-brand-950">5 mins</p>
              <p className="text-sm text-surface-800">To start a claim</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg shadow-brand-900/5 border border-surface-200 text-center">
              <div className="w-10 h-10 bg-accent-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="font-display text-2xl font-bold text-brand-950">Free</p>
              <p className="text-sm text-surface-800">Eligibility check</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg shadow-brand-900/5 border border-surface-200 text-center">
              <div className="w-10 h-10 bg-success-500/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="font-display text-2xl font-bold text-brand-950">£35</p>
              <p className="text-sm text-surface-800">Claim pack</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQAccordionItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onClick={() => toggleFAQ(index)}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Still have questions CTA */}
      <section className="py-16 bg-brand-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-800 to-brand-900 p-8 sm:p-12">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-400/10 rounded-full blur-3xl" />
            
            <div className="relative text-center">
              <div className="w-16 h-16 bg-accent-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-accent-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h2 className="font-display text-3xl font-bold text-white mb-4">
                Still have questions?
              </h2>
              <p className="text-brand-200 mb-8 max-w-lg mx-auto">
                Can't find the answer you're looking for? Our team is here to help you 
                understand the claims process and get started.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-brand-700 font-semibold rounded-xl hover:bg-brand-50 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Email Us
                </Link>
                <Link
                  to="/check"
                  className="inline-flex items-center justify-center px-6 py-3 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-colors"
                >
                  Start Your Claim
                  <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom diagonal stripe bar */}
      <div 
        className="h-8"
        style={{
          background: `repeating-linear-gradient(
            -45deg,
            #1e40af,
            #1e40af 40px,
            #f97316 40px,
            #f97316 80px
          )`
        }}
      />
    </div>
  );
}

// Accordion Item Component
function FAQAccordionItem({ 
  question, 
  answer, 
  isOpen, 
  onClick,
  index 
}: { 
  question: string; 
  answer: React.ReactNode; 
  isOpen: boolean; 
  onClick: () => void;
  index: number;
}) {
  return (
    <div 
      className={`rounded-2xl border-2 transition-all duration-300 ${
        isOpen 
          ? 'border-brand-200 bg-brand-50/50 shadow-lg shadow-brand-900/5' 
          : 'border-surface-200 bg-white hover:border-brand-100 hover:shadow-md'
      }`}
    >
      <button
        onClick={onClick}
        className="w-full px-6 py-5 flex items-center justify-between text-left"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-4">
          <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold transition-colors ${
            isOpen 
              ? 'bg-accent-500 text-white' 
              : 'bg-surface-100 text-surface-800'
          }`}>
            {index + 1}
          </span>
          <span className={`font-display text-lg font-semibold transition-colors ${
            isOpen ? 'text-brand-900' : 'text-brand-950'
          }`}>
            {question}
          </span>
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
          isOpen 
            ? 'bg-brand-600 rotate-180' 
            : 'bg-surface-100'
        }`}>
          <svg 
            className={`w-5 h-5 transition-colors ${isOpen ? 'text-white' : 'text-surface-800'}`} 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${
        isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="px-6 pb-6 pl-18 text-surface-800">
          <div className="pl-12 border-l-2 border-brand-200">
            {answer}
          </div>
        </div>
      </div>
    </div>
  );
}
